/**
 * Typed client for the Villamine AI API used by the widget. Every call has a
 * timeout and maps failures to a small set of error kinds the UI can show.
 */
import type { SearchRequest, SearchResponse } from "../domain/api";
import type { ResolvedConfig } from "./config";

export type ApiErrorKind = "network" | "timeout" | "rate_limited" | "server" | "invalid";

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: ApiErrorKind; status: number | null };

function url(cfg: ResolvedConfig, path: string): string {
  return `${cfg.apiBaseUrl}${path}`;
}

async function post<T>(cfg: ResolvedConfig, path: string, body: unknown, signal?: AbortSignal): Promise<ApiResult<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), cfg.requestTimeoutMs);
  const onAbort = () => controller.abort();
  signal?.addEventListener("abort", onAbort);
  try {
    const res = await fetch(url(cfg, path), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
      credentials: "omit",
    });
    let data: T;
    try {
      data = (await res.json()) as T;
    } catch {
      if (res.status === 429) return { ok: false, error: "rate_limited", status: 429 };
      return { ok: false, error: "invalid", status: res.status };
    }
    // 429 (hosting-side IP rate limit or the per-process burst limiter) and 503: one generic, localized
    // "temporarily unavailable" text; no threshold or rule is revealed. 400: the request was rejected
    // before the AI pipeline.
    if (res.status === 429 || res.status === 503) return { ok: false, error: "rate_limited", status: res.status };
    if (res.status === 400) return { ok: false, error: "invalid", status: 400 };
    if (!res.ok && !(data && typeof data === "object" && "status" in data)) return { ok: false, error: "server", status: res.status };
    return { ok: true, data };
  } catch (err) {
    if (signal?.aborted) throw err;
    if ((err as Error).name === "AbortError") return { ok: false, error: "timeout", status: null };
    return { ok: false, error: "network", status: null };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}

export function postSearch(cfg: ResolvedConfig, request: SearchRequest & { visitor_id: string }, signal?: AbortSignal): Promise<ApiResult<SearchResponse>> {
  return post<SearchResponse>(cfg, "/api/villamine-ai/search", request, signal);
}

export interface VerbalizeResponse {
  text: string;
  source: "llm" | "plan";
  violations: string[];
}

/** Deferred reply translation for a plan_id returned by a search (experimental). */
export function postVerbalize(cfg: ResolvedConfig, planId: string, visitorId: string, signal?: AbortSignal): Promise<ApiResult<VerbalizeResponse>> {
  return post<VerbalizeResponse>(cfg, "/api/villamine-ai/verbalize", { plan_id: planId, visitor_id: visitorId }, signal);
}

export function postEvent(cfg: ResolvedConfig, event: { type: "result_click"; villa_id: string; position: number; language: string | null }): void {
  try {
    void fetch(url(cfg, "/api/villamine-ai/event"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
      keepalive: true,
      credentials: "omit",
    }).catch(() => undefined);
  } catch {
    /* analytics must never break the widget */
  }
}
